import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND_META, useBrand } from "../context/BrandContext";
import { SALES_SUBMIT_ROLES } from "../lib/roles";
import SubmitReport from "../components/reporting/SubmitReport";
import DailyReportForm from "../components/reporting/DailyReportForm";
import JubelioVerification from "../components/reporting/JubelioVerification";
import GroupClosing from "../components/reporting/GroupClosing";
import ExecutiveDashboard from "../components/reporting/dashboard/ExecutiveDashboard";
import PoBatches from "../components/reporting/PoBatches";
import ReportsList from "../components/reporting/ReportsList";
import PoLedger from "../components/reporting/PoLedger";
import ReportingV2 from "../components/reporting/v2/ReportingV2";
import ModeSwitch from "../components/reporting/ModeSwitch";

const inp = "w-full border border-mist rounded-sm px-3 py-2 bg-white text-base md:text-sm focus:outline-none focus:border-sage-dark";
const fmtIDR = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const HARI = { Monday: "Senin", Tuesday: "Selasa", Wednesday: "Rabu", Thursday: "Kamis", Friday: "Jumat", Saturday: "Sabtu", Sunday: "Minggu" };

const STATE_META = {
    NOT_REQUIRED: { label: "Tidak wajib lapor", cls: "bg-neutral-100 text-neutral-500" },
    OPEN: { label: "Dibuka", cls: "bg-sage/15 text-sage-dark" },
    DEADLINE_PASSED: { label: "Lewat batas waktu", cls: "bg-amber-50 text-amber-700" },
};

function StatePill({ state, testId }) {
    const m = STATE_META[state] || STATE_META.OPEN;
    return (
        <span data-testid={testId} className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${m.cls}`}>
            {m.label}
        </span>
    );
}

function WeekStrip({ brand, days }) {
    return (
        <div className="grid grid-cols-7 gap-1.5" data-testid={`schedule-week-${brand}`}>
            {days.map((d) => (
                <div
                    key={d.reporting_date}
                    data-testid={`schedule-day-${brand}-${d.reporting_date}`}
                    className={`border rounded-sm px-1.5 py-2 text-center ${d.required ? "border-mist bg-white" : "border-dashed border-mist bg-neutral-50"}`}
                >
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400">{(HARI[d.weekday] || d.weekday).slice(0, 3)}</div>
                    <div className="font-serif text-lg leading-tight">{d.reporting_date.slice(8)}</div>
                    <div className={`text-[9px] uppercase tracking-wider mt-0.5 ${d.required ? "text-sage-dark" : "text-neutral-400"}`}>
                        {d.required ? "Lapor" : "Libur"}
                    </div>
                </div>
            ))}
        </div>
    );
}

function SettingsEditor({ s, onSaved }) {
    const [deadline, setDeadline] = useState(s.deadline);
    const [tol, setTol] = useState(s.reconciliation_tolerance_idr);
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/reporting/settings/${s.brand}`, {
                deadline,
                reconciliation_tolerance_idr: Math.max(0, parseInt(tol, 10) || 0),
            });
            toast.success("Pengaturan laporan disimpan");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end pt-4 border-t border-mist" data-testid={`settings-editor-${s.brand}`}>
            <label className="text-xs text-neutral-500">
                Batas waktu (WIB)
                <input type="time" value={deadline} onChange={(e) => setDeadline(e.target.value)} className={`${inp} mt-1`} data-testid={`deadline-input-${s.brand}`} />
            </label>
            <label className="text-xs text-neutral-500">
                Toleransi selisih rekonsiliasi (Rp)
                <input type="number" min="0" value={tol} onChange={(e) => setTol(e.target.value)} className={`${inp} mt-1`} data-testid={`tolerance-input-${s.brand}`} />
            </label>
            <button
                type="button"
                onClick={save}
                disabled={busy}
                data-testid={`save-settings-${s.brand}`}
                className="bg-sage-dark text-white px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-50 transition-colors"
            >
                {busy ? "Menyimpan…" : "Simpan"}
            </button>
        </div>
    );
}

function BrandCard({ s, isOwner, onSaved }) {
    const [sched, setSched] = useState(null);
    useEffect(() => {
        api.get("/reporting/schedule", { params: { brand: s.brand, days: 7 } })
            .then(({ data }) => setSched(data))
            .catch(() => setSched(null));
    }, [s.brand, s.deadline]);
    const today = sched?.days?.[0];
    const name = BRAND_META[s.brand]?.name || s.brand;
    return (
        <section className="bg-white/70 border border-mist rounded-sm p-5 md:p-6 space-y-5" data-testid={`reporting-brand-${s.brand}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">Brand</div>
                    <h2 className="font-serif text-2xl">{name}</h2>
                </div>
                {today && <StatePill state={today.state} testId={`today-state-${s.brand}`} />}
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Item label="Hari ini (WIB)" value={today ? `${HARI[today.weekday] || today.weekday}, ${today.reporting_date}` : "—"} testId={`today-date-${s.brand}`} />
                <Item label="Hari lapor" value="Senin – Sabtu" testId={`report-days-${s.brand}`} />
                <Item label="Batas waktu" value={`${s.deadline} WIB`} testId={`deadline-value-${s.brand}`} />
                <Item label="Toleransi" value={fmtIDR(s.reconciliation_tolerance_idr)} testId={`tolerance-value-${s.brand}`} />
            </dl>
            {sched && <WeekStrip brand={s.brand} days={sched.days} />}
            <p className="text-xs text-neutral-400">Minggu: tidak wajib lapor — tidak pernah dianggap belum lapor atau terlambat.</p>
            {isOwner && <SettingsEditor key={`${s.deadline}-${s.reconciliation_tolerance_idr}`} s={s} onSaved={onSaved} />}
        </section>
    );
}

function Item({ label, value, testId }) {
    return (
        <div>
            <dt className="text-[10px] uppercase tracking-widest text-neutral-400">{label}</dt>
            <dd className="mt-1 text-ink" data-testid={testId}>{value}</dd>
        </div>
    );
}

function Tabs({ tabs, active, onChange }) {
    return (
        <div className="flex gap-1 border-b border-mist overflow-x-auto" data-testid="reporting-tabs">
            {tabs.map(([k, label]) => (
                <button key={k} type="button" onClick={() => onChange(k)} data-testid={`reporting-tab-${k}`}
                    className={`px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${active === k ? "text-ink border-b-2 border-sage-dark" : "text-neutral-500 hover:text-ink"}`}>
                    {label}
                </button>
            ))}
        </div>
    );
}

export function ScheduleTab() {
    const { user } = useAuth();
    const [items, setItems] = useState(null);
    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/reporting/settings");
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Gagal memuat data laporan");
            setItems([]);
        }
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    if (items === null) return <div className="text-sm text-neutral-400" data-testid="reporting-loading">Memuat…</div>;
    return (
        <div className="grid lg:grid-cols-2 gap-6">
            {items.map((s) => <BrandCard key={s.brand} s={s} isOwner={user?.role === "owner"} onSaved={load} />)}
        </div>
    );
}

export default function Reporting() {
    const [mode, setMode] = useState(null);
    const [failed, setFailed] = useState(false);
    const load = () => {
        setFailed(false);
        api.get("/reporting/mode").then((r) => {
            if (r.data?.mode === "GROUP" || r.data?.mode === "LEGACY") setMode(r.data.mode);
            else setFailed(true);
        }).catch(() => setFailed(true));
    };
    useEffect(() => { load(); }, []);
    if (failed) {
        return (
            <div className="border border-rose-200 bg-rose-50/50 rounded-sm p-4 space-y-3 max-w-md" data-testid="reporting-load-error">
                <p className="text-sm text-rose-800">Gagal memuat Reporting. Silakan coba lagi.</p>
                <button type="button" onClick={load} className="border border-mist bg-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest" data-testid="reporting-retry-btn">Coba Lagi</button>
            </div>
        );
    }
    if (!mode) return <div className="text-sm text-neutral-500" data-testid="reporting-loading">Memuat…</div>;
    return mode === "GROUP" ? <ReportingV2 /> : <ReportingTabs mode={mode} />;
}

function ReportingTabs({ mode }) {
    const { user } = useAuth();
    const { current } = useBrand();
    const canSubmit = SALES_SUBMIT_ROLES.includes(user?.role);
    const group = mode === "GROUP";
    const tabs = group ? [
        ...(user?.role === "owner" ? [["dashboard", "Dashboard"]] : []),
        ["group", "Closing Grup"],
        ["batches", "Batch PO"],
        ["reports", "Riwayat Laporan"],
        ["po", "Buku PO"],
        ...(canSubmit ? [["submit", "Laporan Detail (Excel)"]] : []),
        ...(user?.role === "owner" ? [["jubelio", "Cek Jubelio"]] : []),
        ["schedule", "Jadwal"],
    ] : [
        ...(canSubmit ? [["daily", "Laporan Harian"]] : []),
        ["reports", "Riwayat Laporan"],
        ["po", "Buku PO"],
        ...(canSubmit ? [["submit", "Laporan Detail (Excel)"]] : []),
        ...(user?.role === "owner" ? [["jubelio", "Cek Jubelio"]] : []),
        ["schedule", "Jadwal"],
        ...(user?.role === "owner" ? [["mode", "Mode Laporan"]] : []),
    ];
    const [tab, setTab] = useState(tabs[0][0]);
    const [refreshKey, setRefreshKey] = useState(0);
    const brandName = BRAND_META[current]?.name || current;
    return (
        <div className="space-y-8" data-testid="reporting-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">Internal · Asia/Jakarta · {brandName}</div>
                <h1 className="font-serif text-4xl md:text-5xl mt-1 tracking-tight">Laporan Harian</h1>
                <p className="text-sm text-neutral-500 mt-2 max-w-xl" data-testid="reporting-mode-text">
                    {group
                        ? "Closing Harian H&T Group mencakup Heels & Tea dan Hoshizora sekaligus. Sales dan Finance mengisi laporan masing-masing secara terpisah, Senin–Sabtu sebelum pukul 18.00 WIB. Minggu: tanpa batas waktu."
                        : "Admin Sales mengisi Laporan Harian Senin–Sabtu sebelum batas waktu di tab Jadwal, dan masih bisa mengoreksinya sampai Owner menyetujui. Minggu: tidak wajib lapor."}
                </p>
            </div>
            <Tabs tabs={tabs} active={tab} onChange={setTab} />
            {group && tab === "dashboard" && <ExecutiveDashboard />}
            {group && tab === "group" && <GroupClosing />}
            {group && tab === "batches" && <PoBatches />}
            {tab === "daily" && <DailyReportForm key={current} brand={current} onSubmitted={() => { setRefreshKey((k) => k + 1); setTab("reports"); }} />}
            {tab === "submit" && <SubmitReport brand={current} onSubmitted={() => { setRefreshKey((k) => k + 1); setTab("reports"); }} />}
            {tab === "reports" && <ReportsList brand={current} refreshKey={refreshKey} />}
            {tab === "po" && <PoLedger brand={current} refreshKey={refreshKey} />}
            {tab === "jubelio" && <JubelioVerification />}
            {tab === "schedule" && <ScheduleTab />}
            {tab === "mode" && user?.role === "owner" && <ModeSwitch />}
        </div>
    );
}
